<template>
  <div
    class="tw-ml-2 tw-rounded tw-w-full tw-items-center"
    :class="[
      mStep.isCurrent ? 'tw-px-3 tw-py-6' : 'tw-p-3',
      mStep.params.upComing
        ? 'tw-bg-[#f2f2f2]'
        : 'tw-border-[1px] tw-border-solid tw-bg-white tw-border-gray-200',
    ]"
  >
    <div class="tw-w-full tw-flex tw-items-center tw-justify-between">
      <div class="tw-gap-x-2">
        <div class="tw-flex tw-gap-x-2 tw-items-center">
          <WdsTag
            v-if="mStep.useTagForName"
            sm
            gray
            class="!tw-bg-gray-300 !tw-rounded-full !tw-text-gray-800"
          >
            <div class="tw-max-w-36 tw-truncate">
              {{ mStep.name }}
            </div>
          </WdsTag>
          <WdsText v-else large>
            <span :class="{ 'tw-text-gray-700': mStep.params.upComing }" v-text="mStep.name" />
          </WdsText>
          <WdsTag
            v-if="mStep.params.status"
            sm
            class="!tw-rounded"
            :success="mStep.params.status.state === ApprovalApprovableState.Approved"
            :error="
              mStep.params.status.state === ApprovalApprovableState.Rejected ||
              mStep.params.status.state === ApprovalApprovableState.Cancelled
            "
            :warning="mStep.params.status.state === ApprovalApprovableState.Requested"
          >
            {{ mStep.params.status.label }}
          </WdsTag>
          <WdsText v-if="mStep.params.date && !mStep.params.upComing && !mStep.isCurrent" secondary>
            {{ mStep.params.date }}
          </WdsText>
        </div>
        <WdsText v-if="mStep.isCurrent" secondary class="tw-mt-2">
          {{ $t('Delivered :date', { date: mStep.params.date }) }}
        </WdsText>
      </div>

      <div v-if="!mStep.isCurrent" class="tw-flex tw-space-x-2 tw-items-center">
        <WdsAvatarLabel
          v-if="mStep.params.user"
          :text="mStep.params.user.name"
          :path="getAvatarPath(mStep.params.user.avatar, mStep.params.user.id)"
          :initials="textHelpers.nameStringToInitials(mStep.params.user.name, 2)"
          truncated
          sm
        />
        <WdsText v-else secondary>-</WdsText>
      </div>

      <div v-else class="tw-flex tw-items-center">
        <WdsTooltip class="tw-mr-9">
          <WdsAvatarGroup
            v-if="mStep.params.userGroup"
            sm
            :avatars="
              mStep.params.userGroup.users.data.map((user: any) => ({
                path: getAvatarPath(user.avatar, user.id),
                initials: textHelpers.nameStringToInitials(user.name, 2),
              }))
            "
            :max-avatars="10"
            :total="mStep.params.userGroup.users.paginatorInfo.total"
          />
          <template #tooltip-content>
            <WdsStack class="tw-mb-1 tw-font-bold" align-center gap-sm>{{ mStep.name }} :</WdsStack>
            <div
              v-for="user in mStep.params.userGroup.users.data"
              :key="`user_tooltip_${user.id}`"
              style="text-align: left"
              v-text="user.name"
            />
          </template>
        </WdsTooltip>
        <template v-if="viewerCanAction">
          <div
            class="tw-absolute tw-top-0 tw-right-[13rem] tw-h-[98px] tw-border-r-[1px] tw-border-l-0 tw-border-solid tw-border-gray-200"
          ></div>
          <WdsButton
            primary
            data-test="review-approval-button"
            class="!tw-mr-6"
            @click.prevent="emit('show-approval-request-modal')"
          >
            {{ $t('Review approval') }}
          </WdsButton>
        </template>
      </div>
    </div>
    <div v-if="mStep.params.message" class="tw-rounded tw-mt-2 tw-bg-[#F6F6F6] tw-px-4 tw-py-2">
      <p class="tw-m-0 tw-text-gray-800" v-text="mStep.params.message" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue'
import {
  WdsAvatarGroup,
  WdsAvatarLabel,
  WdsButton,
  WdsStack,
  WdsTag,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { ApprovalApprovableState } from '@/gql/graphql'
import { textHelpers } from '@/utils/tools'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import type { HireCreatedApprovableProgressStep } from '@/components/Workflows/Approvals/approvals-types'
import { getAvatarPath } from '@/utils/avatar'

defineProps({
  mStep: {
    type: Object as PropType<HireCreatedApprovableProgressStep>,
    required: true,
  },
  viewerCanAction: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['show-approval-request-modal'])
</script>
